import {
  FormLabel,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
} from '@mui/material';
import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './NumberField.scss';

const AppNumberField: FC<AppNumberFieldProps> = ({
  id,
  label,
  required,
  isRow = false,
  placeholder,
  value: initValue,
  range,
  isDecima = false,
  hideSpinner = false,
  onChange,
  onChangeSize,
  error,
}) => {
  const { t } = useTranslation();

  const [value, setValue] = useState<string | undefined>();
  const [isBlur, setIsBlur] = useState<boolean>(false);

  const errorText = useMemo(() => {
    if (error?.type === 'required' && !error.message) {
      return t('message.MSE00001', { 0: t(label!) });
    }

    return error?.message ? t(error?.message) : null;
  }, [error]);

  const preventCharacters = useMemo(() => {
    return isDecima ? ['e', 'E', '-', ','] : ['e', 'E', '-', '.', ','];
  }, [isDecima]);

  useEffect(() => {
    setValue(initValue?.toString());
  }, [initValue]);

  useEffect(() => {
    if (!isBlur) return;
    const fixedValue = Number(Number(value).toFixed(2));
    if (fixedValue || fixedValue === 0) {
      setValue(isDecima ? fixedValue.toString() : value);
      onChange?.(Number(fixedValue));
    } else {
      setValue(undefined);
      onChange?.(undefined);
    }
    setIsBlur(false);
  }, [isBlur]);

  const handleIncrease = () => {
    if (!range?.max || Number(value ?? 0) < range.max) {
      const newValue = Number(value ?? 0) + 1;
      setValue(newValue.toString());
      onChange?.(newValue);
    }
  };

  const handleDecrease = () => {
    if (!range?.min || Number(value ?? 0) > range.min) {
      if (value ?? 0) {
        const newValue = Number(value ?? 0) - 1;
        setValue(newValue.toString());
        onChange?.(newValue);
      }
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setValue(value ? value : undefined);
    if (onChangeSize) onChangeSize?.(event);
  };

  const handleBlur = () => {
    if (range?.min && Number(value ?? 0) < range.min) {
      setValue(range.min.toString());
    } else if (range?.max && Number(value ?? 0) > range.max) {
      setValue(range.max.toString());
    }
    setIsBlur(true);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (preventCharacters.includes(event.key)) {
      event.preventDefault();
    }
  };

  return (
    <Grid
      container
      className="NumberField"
      direction={isRow ? 'row' : 'column'}
      spacing={isRow ? 3 : 2}
    >
      {label && (
        <Grid
          item
          xs={4}
          alignItems="center"
          justifyContent={isRow ? 'flex-end' : 'flex-start'}
        >
          <FormLabel htmlFor={id} required={required}>
            {t(label)}
          </FormLabel>
        </Grid>
      )}
      <Grid item xs={8}>
        <TextField
          id={id}
          name={id}
          type="number"
          value={value !== undefined ? String(value) : undefined}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          placeholder={t(placeholder!)}
          inputProps={{
            sx: { textAlign: hideSpinner ? 'left' : 'center' },
            step: isDecima ? 'any' : 1,
            ...range,
          }}
          InputProps={{
            startAdornment: !hideSpinner && (
              <InputAction
                position="start"
                content="-"
                onClick={handleDecrease}
              />
            ),
            endAdornment: !hideSpinner && (
              <InputAction
                position="end"
                content="+"
                onClick={handleIncrease}
              />
            ),
          }}
          error={!!error?.type}
          helperText={errorText}
        />
      </Grid>
    </Grid>
  );
};

interface InputActionProps {
  position: 'start' | 'end';
  content: string;
  onClick: React.MouseEventHandler<HTMLButtonElement>;
}

const InputAction: FC<InputActionProps> = ({ position, content, onClick }) => (
  <InputAdornment position={position}>
    <IconButton tabIndex={-1} edge={position} onClick={onClick}>
      {content}
    </IconButton>
  </InputAdornment>
);

export default AppNumberField;
